import { useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, Plus, Bell, UserPlus, Banknote, Footprints } from "lucide-react";
import { toast } from "sonner";
import api, { peso, fmtErr, todayStr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ReceiptModal from "../../components/ReceiptModal";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";

const TIER_COLORS = { Premium: "border-l-[#E84855]", Standard: "border-l-[#0E7C84]", Express: "border-l-[#F4A261]", Specialty: "border-l-[#2A9D8F]" };
const STATUS_BADGE = { booked: "bg-blue-100 text-blue-700", confirmed: "bg-emerald-100 text-emerald-700", in_progress: "bg-amber-100 text-amber-700", completed: "bg-slate-100 text-slate-600", cancelled: "bg-red-100 text-red-600", no_show: "bg-red-100 text-red-600" };

function shiftDate(d, days) {
  const dt = new Date(d);
  dt.setDate(dt.getDate() + days);
  return dt.toISOString().slice(0, 10);
}

export default function Grooming() {
  const { user } = useAuth();
  const [date, setDate] = useState(todayStr());
  const [appointments, setAppointments] = useState([]);
  const [groomers, setGroomers] = useState([]);
  const [services, setServices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [bookDialog, setBookDialog] = useState(false);
  const [form, setForm] = useState({ customer_id: "", pet_id: "", service_id: "", groomer_id: "", time: "", notes: "", walk_in: false });
  const [pets, setPets] = useState([]);
  const [slots, setSlots] = useState([]);
  const [completeAppt, setCompleteAppt] = useState(null);
  const [payMethod, setPayMethod] = useState("cash");
  const [receiptId, setReceiptId] = useState(null);

  const load = () => {
    api.get("/grooming/appointments", { params: { date } }).then((r) => setAppointments(r.data)).catch((e) => toast.error(fmtErr(e)));
    api.get("/grooming/commissions").then((r) => setCommissions(r.data)).catch(() => {});
  };
  useEffect(() => { load(); }, [date]);
  useEffect(() => {
    api.get("/groomers").then((r) => setGroomers(r.data)).catch(() => {});
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
    api.get("/customers").then((r) => setCustomers(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setPets([]);
    if (form.customer_id) {
      api.get(`/customers/${form.customer_id}`).then((r) => setPets(r.data.pets)).catch(() => {});
    }
  }, [form.customer_id]);

  useEffect(() => {
    setSlots([]);
    if (form.service_id && date) {
      api.get("/grooming/slots", { params: { date, service_id: form.service_id, groomer_id: form.groomer_id || "" } })
        .then((r) => setSlots(r.data)).catch(() => {});
    }
  }, [form.service_id, form.groomer_id, date, bookDialog]);

  const book = async () => {
    try {
      await api.post("/grooming/appointments", { ...form, groomer_id: form.groomer_id || null, date });
      toast.success("Appointment booked");
      setBookDialog(false);
      setForm({ customer_id: "", pet_id: "", service_id: "", groomer_id: "", time: "", notes: "", walk_in: false });
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const setStatus = async (id, status) => {
    try { await api.put(`/grooming/appointments/${id}`, { status }); toast.success(`Marked ${status.replace("_", " ")}`); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const complete = async () => {
    try {
      const { data } = await api.post(`/grooming/appointments/${completeAppt.id}/complete`, {
        payments: [{ method: payMethod, amount: completeAppt.price }],
      });
      toast.success(`Service completed${data.commission ? ` — commission ${peso(data.commission.amount)}` : ""}`);
      setReceiptId(data.sale.id);
      setCompleteAppt(null);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  const remind = async (id) => {
    try { const { data } = await api.post(`/grooming/appointments/${id}/remind`); toast.success(data.message); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const columns = [...groomers.map((g) => ({ id: g.id, name: g.name })), { id: null, name: "Unassigned" }];
  const totalCommission = commissions.reduce((s, c) => s + c.amount, 0);

  return (
    <div className="animate-rise space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight" data-testid="grooming-title">Grooming Studio</h1>
          <p className="text-sm text-slate-500">Calendar, groomer lanes and commissions.</p>
        </div>
        <Button className="bg-[#E84855] hover:bg-[#C62836]" onClick={() => setBookDialog(true)} data-testid="new-appointment-btn">
          <Plus className="w-4 h-4 mr-2" /> New Appointment
        </Button>
      </div>

      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={() => setDate(shiftDate(date, -1))} data-testid="date-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <Input type="date" className="w-40 bg-white" value={date} onChange={(e) => setDate(e.target.value)} data-testid="grooming-date" />
        <Button variant="outline" size="sm" onClick={() => setDate(shiftDate(date, 1))} data-testid="date-next"><ChevronRight className="w-4 h-4" /></Button>
        <Button variant="ghost" size="sm" onClick={() => setDate(todayStr())} data-testid="date-today">Today</Button>
        <span className="ml-auto text-sm font-semibold text-[#0E7C84]" data-testid="commission-total">
          Commissions shown: {peso(totalCommission)}
        </span>
      </div>

      <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${columns.length}, minmax(220px, 1fr))`, overflowX: "auto" }}>
        {columns.map((col) => {
          const items = appointments.filter((a) => (a.groomer_id || null) === col.id);
          return (
            <div key={col.id || "none"} className="bg-white rounded-2xl border border-[#BFE8E9] p-3 min-h-[300px]">
              <div className="flex items-center justify-between mb-3 px-1">
                <p className="font-display font-semibold text-sm">{col.name}</p>
                <Badge variant="secondary" className="text-[10px]">{items.length}</Badge>
              </div>
              <div className="space-y-2">
                {items.map((a) => (
                  <div key={a.id} className={`border border-[#BFE8E9] border-l-4 ${TIER_COLORS[a.service_tier] || "border-l-slate-300"} rounded-xl p-3 bg-[#F2FBFB]`} data-testid={`appt-card-${a.id.slice(0, 8)}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-[#0E7C84] text-sm">{a.time}</span>
                      <Badge className={`${STATUS_BADGE[a.status]} hover:${STATUS_BADGE[a.status]} text-[9px] capitalize`}>{a.status.replace("_", " ")}</Badge>
                    </div>
                    <p className="font-semibold text-sm mt-1">{a.pet_name} <span className="font-normal text-slate-400">· {a.pet_breed}</span></p>
                    <p className="text-xs text-slate-500">{a.service_name} · {peso(a.price)}</p>
                    <p className="text-[10px] text-slate-400 capitalize">{a.weight_tier} · {a.coat_type} coat {a.walk_in && "· walk-in"}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {a.status === "booked" && <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setStatus(a.id, "confirmed")} data-testid={`confirm-${a.id.slice(0, 8)}`}>Confirm</Button>}
                      {a.status === "confirmed" && <Button size="sm" variant="outline" className="h-6 text-[10px] px-2" onClick={() => setStatus(a.id, "in_progress")} data-testid={`start-${a.id.slice(0, 8)}`}>Start</Button>}
                      {["confirmed", "in_progress", "booked"].includes(a.status) && (
                        <>
                          <Button size="sm" className="h-6 text-[10px] px-2 bg-[#0E7C84] hover:bg-[#093F46]" onClick={() => { setCompleteAppt(a); setPayMethod("cash"); }} data-testid={`complete-${a.id.slice(0, 8)}`}>Complete</Button>
                          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-1.5" onClick={() => remind(a.id)} data-testid={`remind-${a.id.slice(0, 8)}`}><Bell className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" className="h-6 text-[10px] px-2 text-red-500" onClick={() => setStatus(a.id, "cancelled")} data-testid={`cancel-${a.id.slice(0, 8)}`}>Cancel</Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                {items.length === 0 && <p className="text-xs text-slate-400 text-center py-6">No appointments</p>}
              </div>
            </div>
          );
        })}
      </div>

      {commissions.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#BFE8E9] p-5" data-testid="commission-panel">
          <h3 className="font-display font-semibold mb-3">Commission ledger</h3>
          <div className="space-y-1.5">
            {commissions.slice(0, 8).map((c) => (
              <div key={c.id} className="flex justify-between text-sm border-b border-[#E2F5F5] pb-1.5">
                <span>{c.groomer_name} — {c.service_name} <span className="text-xs text-slate-400">({Math.round(c.rate * 100)}% of {peso(c.service_price)})</span></span>
                <span className="font-bold text-[#0E7C84]">{peso(c.amount)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={bookDialog} onOpenChange={setBookDialog}>
        <DialogContent className="max-w-lg" data-testid="book-appointment-dialog">
          <DialogHeader><DialogTitle className="font-display">New appointment — {date}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Customer</Label>
              <Select value={form.customer_id} onValueChange={(v) => setForm({ ...form, customer_id: v, pet_id: "" })}>
                <SelectTrigger data-testid="appt-customer"><SelectValue placeholder="Select customer" /></SelectTrigger>
                <SelectContent>{customers.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Pet</Label>
              <Select value={form.pet_id} onValueChange={(v) => setForm({ ...form, pet_id: v })} disabled={!form.customer_id}>
                <SelectTrigger data-testid="appt-pet"><SelectValue placeholder="Select pet" /></SelectTrigger>
                <SelectContent>{pets.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.weight_tier})</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Service</Label>
              <Select value={form.service_id} onValueChange={(v) => setForm({ ...form, service_id: v })}>
                <SelectTrigger data-testid="appt-service"><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>{services.map((s) => <SelectItem key={s.id} value={s.id}>{s.name} — {peso(s.base_price)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Groomer</Label>
              <Select value={form.groomer_id} onValueChange={(v) => setForm({ ...form, groomer_id: v })}>
                <SelectTrigger data-testid="appt-groomer"><SelectValue placeholder="Any available" /></SelectTrigger>
                <SelectContent>{groomers.map((g) => <SelectItem key={g.id} value={g.id}>{g.name} ({Math.round((g.commission_rate || 0) * 100)}%)</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Time</Label>
              <div className="grid grid-cols-4 gap-1 max-h-28 overflow-y-auto">
                {slots.map((s) => (
                  <button key={s.time} type="button" disabled={!s.available} onClick={() => setForm({ ...form, time: s.time })} data-testid={`appt-slot-${s.time}`}
                    className={`py-1 rounded text-xs font-medium border disabled:opacity-40 ${form.time === s.time ? "bg-[#0E7C84] text-white" : ""}`}>
                    {s.time}
                  </button>
                ))}
              </div>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="appt-notes" /></div>
            <label className="col-span-2 flex items-center gap-2 text-sm cursor-pointer" data-testid="appt-walkin">
              <input type="checkbox" checked={form.walk_in} onChange={(e) => setForm({ ...form, walk_in: e.target.checked })} className="accent-[#0E7C84]" />
              Walk-in customer (queue)
            </label>
          </div>
          <Button className="bg-[#E84855] hover:bg-[#C62836] mt-2" onClick={book} disabled={!form.customer_id || !form.pet_id || !form.service_id || !form.time} data-testid="appt-save-btn">
            Book appointment
          </Button>
        </DialogContent>
      </Dialog>

      <Dialog open={!!completeAppt} onOpenChange={(v) => !v && setCompleteAppt(null)}>
        <DialogContent data-testid="complete-dialog">
          <DialogHeader><DialogTitle className="font-display">Complete &amp; collect payment</DialogTitle></DialogHeader>
          {completeAppt && (
            <div className="space-y-4">
              <div className="bg-[#E2F5F5] rounded-xl p-4">
                <p className="font-semibold">{completeAppt.service_name} — {completeAppt.pet_name}</p>
                <p className="font-display font-bold text-2xl text-[#0E7C84] mt-1">{peso(completeAppt.price)}</p>
              </div>
              <RadioGroup value={payMethod} onValueChange={setPayMethod} className="grid grid-cols-3 gap-2">
                {[["cash", "Cash"], ["gcash", "GCash"], ["card", "Card"]].map(([v, l]) => (
                  <label key={v} className={`flex items-center justify-center gap-2 border rounded-xl py-2.5 text-sm font-medium cursor-pointer ${payMethod === v ? "border-[#0E7C84] bg-[#D9F7F8]" : ""}`} data-testid={`complete-pay-${v}`}>
                    <RadioGroupItem value={v} className="sr-only" />{l}
                  </label>
                ))}
              </RadioGroup>
              <Button className="w-full bg-[#0E7C84] hover:bg-[#093F46]" onClick={complete} data-testid="complete-confirm-btn">
                Collect {peso(completeAppt.price)}
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ReceiptModal saleId={receiptId} open={!!receiptId} onClose={() => setReceiptId(null)} allowEmail />
    </div>
  );
}
